import { hash } from '@ember/helper';
import { service } from '@ember/service';

import { load } from 'ember-async-data';
import CpPanel from 'ember-collapsible-panel/components/cp-panel/component';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';

import Background from 'umg-ember-backend/components/page-builder/options/form/section/background';
import CustomOptions from 'umg-ember-backend/components/page-builder/options/form/section/custom-options';
import Footer from 'umg-ember-backend/components/page-builder/options/form/section/footer';
import Header from 'umg-ember-backend/components/page-builder/options/form/section/header';
import Headline from 'umg-ember-backend/components/page-builder/options/form/section/headline';
import Text from 'umg-ember-backend/components/page-builder/options/form/section/text';
import Component from 'umg-ember-backend/components/record-detail/forms';
import Newsletter from 'umg-ember-backend/components/record-detail/forms/newsletter';
import AssetPreview from 'umg-ember-backend/components/ui/asset-preview';
import recordHeadlineOrNew from 'umg-ember-backend/helpers/record-headline-or-new';

export default class PageBuilderOptionsFormForPageBuilderComponent extends Component {
  @service pageBuilderFrontendApi;
  @service googleFonts;

  // Defaults
  templateOptions = [
    {
      value: 'standard',
      label: 'Standard',
    },
    {
      value: 'custom',
      label: 'Custom',
    },
  ];

  versionOptions = [
    {
      value: '1',
      label: 'Version 1.0 (Laravel)',
    },
    {
      value: '2',
      label: 'Version 2.0 (Next.js)',
    },
  ];

  // Getter and setter
  get themeOptions() {
    return this.pageBuilderFrontendApi.loadThemes.value ?? [];
  }

  get selectedFonts() {
    return this.args.form.model.get('options.fontFamiliesToLoad');
  }

  get isChannelVariant() {
    return this.recordVariants.isChannelVariant(this.args.mlp);
  }

  // Functions
  selectFonts = (fonts) => {
    this.args.form.model.set('options.fontFamiliesToLoad', fonts);

    const firstFont = fonts[0]?.familyWithFallback;
    if (!this.args.form.model.options.fontFamily) {
      this.args.form.model.set('options.fontFamily', firstFont);
      this.args.form.model.set('options.fontStyle', 'regular');
    }

    if (!this.args.form.model.options.headlineFontFamily) {
      this.args.form.model.set('options.headlineFontFamily', firstFont);
      this.args.form.model.set('options.headlineFontStyle', 'regular');
    }
  };

  // Template
  <template>
    <@form.fieldset
      @titleKey='general'
      @isClosed={{not @mlp.isNew}}
      @showSelectedAttributesIndicator={{true}}
      as |fieldset|
    >
      <fieldset.field
        @property='cmsState'
        @type='select-value'
        @options={{this.cmsStateOptions}}
      />

      <fieldset.field
        @property='artist'
        @type='belongs-to'
        @on={{hash openNewRecord=false}}
      />

      {{#if @mlp.isV2}}
        <fieldset.field
          @property='channel'
          @type='belongs-to'
          @on={{hash openNewRecord=false}}
        />
      {{/if}}

      <fieldset.field
        @property='ownedByChannel'
        @type='belongs-to'
        @on={{hash openNewRecord=false}}
      />

      <fieldset.field @property='multiPage' @type='checkbox' />
    </@form.fieldset>

    <@form.fieldset
      @titleKey='themeSetup'
      @isClosed={{true}}
      @showSelectedAttributesIndicator={{true}}
      as |fieldset|
    >
      {{#if @mlp.isV2}}
        <fieldset.field
          @property='options.fontFamiliesToLoad'
          @type='select-multiple'
          @labelProperty='family'
          @selectPath={{false}}
          @isSearchEnabled={{true}}
          @searchField='family'
          @options={{this.googleFonts.items}}
          @onChange={{this.selectFonts}}
        />
      {{/if}}
    </@form.fieldset>

    {{#unless @mlp.hasCustomTemplate}}
      <Headline
        @form={{@form}}
        @elementConfig={{@elementConfig}}
        @mlp={{@mlp}}
      />
      <Text @form={{@form}} @elementConfig={{@elementConfig}} @mlp={{@mlp}} />
      <Background
        @form={{@form}}
        @elementConfig={{@elementConfig}}
        @mlp={{@mlp}}
      />
      <Header @form={{@form}} @elementConfig={{@elementConfig}} @mlp={{@mlp}} />
      <Footer @form={{@form}} @elementConfig={{@elementConfig}} @mlp={{@mlp}} />
    {{/unless}}

    <@form.fieldset
      @titleKey='favicon'
      @isClosed={{true}}
      @showSelectedAttributesIndicator={{true}}
      as |fieldset|
    >
      <fieldset.field
        @filters={{hash assetFolderId='120'}}
        @property='favicon'
        @options={{hash allowedFolder='120'}}
        @type='belongs-to'
      />
    </@form.fieldset>

    <@form.fieldset
      @titleKey='newsletter'
      @isClosed={{true}}
      @showSelectedAttributesIndicator={{true}}
      as |fieldset|
    >
      <fieldset.field
        @property='newsletter'
        @on={{hash
          openNewRecord=this.newRecord
          openRecordList=false
          openRecord=false
        }}
        @type='belongs-to'
        as |record|
      >
        {{#let (load record) as |result|}}
          {{#if result.isResolved}}
            <CpPanel @open={{true}} as |panel|>
              <panel.toggle>
                {{recordHeadlineOrNew record}}
              </panel.toggle>
              <panel.body>
                <Newsletter
                  @model={{record}}
                  @recordType='newsletter'
                  @newsletterArtist={{@mlp.artist}}
                  @loadLegalContext={{@loadLegalContext}}
                  @showErrors={{true}}
                />
              </panel.body>
            </CpPanel>
          {{else if result.isPending}}
            {{t 'ui.loading'}}
          {{/if}}
        {{/let}}
      </fieldset.field>
    </@form.fieldset>

    <@form.fieldset
      @titleKey='language'
      @isClosed={{true}}
      @showSelectedAttributesIndicator={{true}}
      as |fieldset|
    >
      <fieldset.field
        @property='cmsLanguageId'
        @type='select-value'
        @disabled={{this.isChannelVariant}}
        @options={{this.availableLanguages}}
      />

      <fieldset.field @property='relatedMlps' @type='has-many' />
    </@form.fieldset>

    {{#if @mlp.isV2}}
      <@form.fieldset
        @titleKey='shopify'
        @isClosed={{true}}
        @showSelectedAttributesIndicator={{true}}
        as |fieldset|
      >
        <fieldset.field @property='shopifyStore' @type='belongs-to' />
        <fieldset.field @property='shopifyHasCartPage' @type='checkbox' />
      </@form.fieldset>

    {{/if}}

    {{#unless @mlp.isV2}}
      <@form.fieldset
        @titleKey='nfc'
        @isClosed={{true}}
        @showSelectedAttributesIndicator={{true}}
        as |fieldset|
      >
        <fieldset.field @property='zatapModelIds' @type='string-array' />
      </@form.fieldset>
    {{/unless}}

    <@form.fieldset
      @titleKey='admin'
      @isClosed={{true}}
      @showSelectedAttributesIndicator={{true}}
      as |fieldset|
    >
      <fieldset.field
        @property='version'
        @type='select-value'
        @options={{this.versionOptions}}
      />

      {{#if @mlp.isV2}}
        <fieldset.field
          @property='theme'
          @type='select-value'
          @options={{this.themeOptions}}
        />
      {{/if}}

      {{#unless @mlp.isV2}}
        <fieldset.field
          @property='options.template'
          @type='select-value'
          @options={{this.templateOptions}}
        />
      {{/unless}}

      <fieldset.field
        @filters={{hash type='WP_VHOST_PB_NEXT'}}
        @property='vhost'
        @type='belongs-to'
        @on={{hash openNewRecord=false openRecord=false}}
      />

      {{#unless @mlp.isV2}}
        <fieldset.field @property='isMultiArtistPage' @type='checkbox' />
        <fieldset.field @property='sso' @type='checkbox' />
      {{/unless}}

      <fieldset.field @property='template' @type='checkbox' />

      {{#if @mlp.template}}
        <fieldset.field
          @filters={{hash assetFolderId='2'}}
          @property='thumbnail'
          @options={{hash allowedFolder='100'}}
          @type='belongs-to'
          as |record|
        >
          <AssetPreview @asset={{record}} />
        </fieldset.field>
      {{/if}}
    </@form.fieldset>

    <@form.fieldset
      @titleKey='controls'
      @isClosed={{true}}
      @showSelectedAttributesIndicator={{true}}
      as |fieldset|
    >
      <fieldset.field
        @disabled={{true}}
        @property='cmsLivedate'
        @type='date-time'
      />

      <fieldset.field
        @disabled={{true}}
        @property='cmsKilldate'
        @type='date-time'
      />
    </@form.fieldset>

    <CustomOptions
      @form={{@form}}
      @elementConfig={{@elementConfig}}
      @mlp={{@mlp}}
    />
  </template>
}
